import React, { FocusEvent, KeyboardEvent, MouseEvent } from 'react'
import { styled } from 'styled-components'

const EditingTextArea = styled.textarea`
  resize: none;
  width: 100%;
  cursor: text;
`

export const EditingInputOnClick: React.FC<{
  value: string
  setValue: (value: string) => void
  placeholderValue?: string
  canEdit?: () => boolean
  isValidInput?: (newValue: string) => boolean
  divStyle?: React.CSSProperties
  inputStyle?: React.CSSProperties
  debugDisableBlur?: boolean
}> = ({
  value,
  setValue,
  placeholderValue,
  canEdit,
  isValidInput,
  divStyle,
  inputStyle,
  debugDisableBlur,
}) => {
  const [editing, setEditing] = React.useState(false)

  return !editing ? (
    <div
      style={{ whiteSpace: 'pre-wrap', ...(divStyle ?? {}) }}
      onClick={(e) => {
        if (!!canEdit && !canEdit()) return
        e.stopPropagation()
        setEditing(true)
      }}>
      {value.length || !placeholderValue ? value : placeholderValue}
    </div>
  ) : (
    <EditingInput
      value={value}
      setValue={setValue}
      setEditing={setEditing}
      isValidInput={isValidInput}
      style={inputStyle}
      debugDisableBlur={debugDisableBlur}
    />
  )
}

export const EditingInput: React.FC<{
  value: string
  setValue: (value: string) => void
  setEditing?: (editing: boolean) => void
  isValidInput?: (newValue: string) => boolean
  debugDisableBlur?: boolean
  style?: React.CSSProperties
}> = ({
  value,
  setValue,
  setEditing,
  isValidInput,
  debugDisableBlur,
  style,
}) => {
  const blockMousePropagation = (e: MouseEvent<HTMLTextAreaElement>) => {
    e.stopPropagation()
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget
    requestAnimationFrame(() => {
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + 'px'
    })
    if (e.key === 'Enter' && !(e.shiftKey || e.altKey)) {
      e.preventDefault()
      renameTarget(e.currentTarget.value)
      setEditing?.(false)
    } else if (e.key === 'Escape') {
      setEditing?.(false)
    } else {
      e.stopPropagation()
    }
  }

  const selectValue = (e: FocusEvent<HTMLTextAreaElement>) => {
    e.currentTarget.style.height = e.currentTarget.scrollHeight + 'px'
    e.currentTarget.setSelectionRange(0, e.currentTarget.value.length)
  }

  const renameTarget = async (newValue: string) => {
    if (newValue === value || (!!isValidInput && !isValidInput(newValue))) {
      return
    }

    setValue(newValue)
  }

  return (
    <EditingTextArea
      style={style}
      defaultValue={value}
      onMouseDown={(e) => blockMousePropagation(e)}
      onMouseUp={(e) => blockMousePropagation(e)}
      onKeyDown={(e) => onKeyDown(e)}
      onFocus={(e) => selectValue(e)}
      onBlur={() => !debugDisableBlur && setEditing?.(false)}
      autoFocus={true}
      rows={1}
    />
  )
}
